import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";
import { siteInfo } from "@/mocks/site";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/dahcgpbp14a1h3mr6img";
const HONEYPOT_NAME = "contact_alt";

const SEO_TITLE = "联系与纠错 - 向网报吃瓜提交官方通报线索与内容更正";
const SEO_DESC =
  "在网报吃瓜提交官方通报线索、指出时间线错误或请求核实传言。填写时请附上官方原文链接与发布日期，核实后 24 小时内处理并标注来源。";
const SEO_KEYWORDS = "网报吃瓜纠错,提交官方通报线索,传言核实,联系我们,内容更正";

const submitTypes = [
  "提供官方通报线索",
  "指出时间线内容错误",
  "请求核实某条传言",
  "转载与内容合作",
  "其他事项",
];

const notices = [
  {
    icon: "ri-links-line",
    title: "尽量附上原文链接",
    desc: "有官方原文链接与发布日期，核实速度会快很多。",
  },
  {
    icon: "ri-time-line",
    title: "更正标注修改时间",
    desc: "确认错误后不删旧节点，而是新增说明并标注修改时间。",
  },
  {
    icon: "ri-shield-check-line",
    title: "不处理人身攻击内容",
    desc: "与核实信息无关的投诉、争吵内容不会进入时间线。",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: SEO_TITLE,
    description: SEO_DESC,
    keywords: SEO_KEYWORDS,
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          name: SEO_TITLE,
          description: SEO_DESC,
          url: "https://sxdayun.com.cn/contact",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://sxdayun.com.cn/" },
            { "@type": "ListItem", position: 2, name: "联系与纠错", item: "https://sxdayun.com.cn/contact" },
          ],
        },
      ],
    },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = String(formData.get(HONEYPOT_NAME) || "").trim();
    if (honeypotValue) {
      setStatus("success");
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === HONEYPOT_NAME) return;
      if (typeof value === "string") {
        params.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请检查网络后重试。");
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系与纠错" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              联系与纠错
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              发现官方已经发声但这里没有收录？或者时间线上的时间、主体写错了？把信息发给我们，核实后会补充节点或标注更正。
            </p>
          </div>
        </section>

        <section className="w-full">
          <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <h2 className="font-heading text-lg font-bold text-foreground-950">提交信息</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                带 <span className="text-accent-600">*</span> 的为必填项，请尽量填写官方原文链接，方便快速核实。
              </p>

              {status === "success" ? (
                <div className="mt-6 flex flex-col items-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-12 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                    已收到，感谢反馈
                  </h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                    我们会在核实后补充或更正对应时间线，并标注修改时间。如留下邮箱，处理结果会同步告知。
                  </p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-add-line"></i>
                      </span>
                      再提交一条
                    </button>
                    <Link
                      to="/events"
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-list-check-3"></i>
                      </span>
                      返回事件追踪
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  data-readdy-form="true"
                  onSubmit={handleSubmit}
                  className="mt-6 flex flex-col gap-5"
                >
                  <div className="flex flex-col gap-2">
                    <label htmlFor="type" className="text-sm font-medium text-foreground-800">
                      提交类型 <span className="text-accent-600">*</span>
                    </label>
                    <select
                      id="type"
                      name="type"
                      required
                      defaultValue={submitTypes[0]}
                      className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                    >
                      {submitTypes.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="event_ref" className="text-sm font-medium text-foreground-800">
                      相关事件标题或页面链接
                    </label>
                    <input
                      id="event_ref"
                      name="event_ref"
                      type="text"
                      placeholder="例如：云溪猫耳山景区天价餐饮争议"
                      className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="source_url" className="text-sm font-medium text-foreground-800">
                        官方原文链接
                      </label>
                      <input
                        id="source_url"
                        name="source_url"
                        type="url"
                        placeholder="https://"
                        className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="publish_date" className="text-sm font-medium text-foreground-800">
                        官方发布日期
                      </label>
                      <input
                        id="publish_date"
                        name="publish_date"
                        type="text"
                        placeholder="例如：2026-09-09"
                        className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                      联系邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收处理结果"
                      className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="detail" className="text-sm font-medium text-foreground-800">
                      详细说明 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="detail"
                      name="detail"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你发现的问题或需要补充的官方信息，最多 500 字。"
                      className="resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                    />
                    <span className="text-xs text-foreground-500">最多 500 字</span>
                  </div>

                  <input
                    type="text"
                    name={HONEYPOT_NAME}
                    className="report-verify-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && formError && (
                    <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                        <i className="ri-error-warning-line"></i>
                      </span>
                      <span>{formError}</span>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                      </span>
                      {status === "submitting" ? "提交中…" : "提交反馈"}
                    </button>
                    <p className="text-xs text-foreground-500">
                      提交即表示你提供的链接与说明可供核实使用，我们不会公开你的邮箱。
                    </p>
                  </div>
                </form>
              )}
            </div>

            <aside className="lg:sticky lg:top-24 lg:h-fit">
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">直接联系</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  如果不方便填表，也可以直接发邮件说明，标题建议注明事件名称。
                </p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  rel="nofollow"
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-600 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-secondary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-mail-line"></i>
                  </span>
                  {siteInfo.email}
                </a>
                <p className="mt-3 text-xs text-foreground-500">受理时间：{siteInfo.period}</p>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">提交须知</h2>
                <ul className="mt-4 flex flex-col gap-4">
                  {notices.map((item) => (
                    <li key={item.title} className="flex gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className={`${item.icon} text-base`}></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-900">{item.title}</h3>
                        <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                          {item.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100/60 p-6">
                <h2 className="font-heading text-sm font-bold text-foreground-950">
                  想先自己查一查？
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  辟谣专区已把常见传言与官方澄清逐条对照，先搜索一下可能就有答案。
                </p>
                <Link
                  to="/mythbusting"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-600"
                >
                  前往辟谣专区
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}